import { useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import {
  DEFAULT_INVITE_MODE,
  TEMP_PASSWORD_MIN_LENGTH,
  describeEmailError,
  describePhoneError,
  describeTempPasswordError,
  evaluateOwnerCompleteness,
  readOwnerDraft,
  setInviteMode,
  setOwnerField,
  validateOwnerEmail,
  validateOwnerPhoneOrEmpty,
  validateTempPassword,
  type InviteMode,
} from '../../../lib/tenant-owner-form.js';

/**
 * Step 6 of the tenant provisioning wizard (D1.5.7) — First owner.
 *
 * Per design §7 and §9 the wizard collects:
 *   • email           (required)
 *   • firstName       (required)
 *   • lastName        (required)
 *   • phone           (optional)
 *   • inviteMode      ('magic_link' default | 'temp_password')
 *   • tempPassword    (required only when inviteMode = 'temp_password')
 *
 * Magic-link path: post-commit (D1.6.1 + D1.7.1), the platform queues a
 * branded invite email; the owner sets their own password through the
 * accept-flow. Temp-password path: the platform admin hands the owner a
 * pre-set credential and the owner is forced to rotate on first login.
 *
 * Validation here is inline only; the kit's strict Zod parse runs at
 * commit time (D1.6.2 + D1.6.1).
 */

type StepAdminUserProps = {
  payload: Record<string, unknown>;
  updatePayload: (patch: Record<string, unknown>) => void;
};

export function StepAdminUser({
  payload,
  updatePayload,
}: StepAdminUserProps): JSX.Element {
  const draft = useMemo(() => readOwnerDraft(payload), [payload]);
  const mode: InviteMode = draft.inviteMode ?? DEFAULT_INVITE_MODE;

  // Persist the default invite mode on first mount so the draft round-trips
  // even if the admin never touches the radio.
  useEffect(() => {
    if (!draft.inviteMode) {
      updatePayload({ owner: setInviteMode(draft, DEFAULT_INVITE_MODE) });
    }
    // run-once seed; subsequent edits flow through onModeChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onField = (key: 'email' | 'firstName' | 'lastName' | 'phone') =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const next = setOwnerField(readOwnerDraft(payload), key, e.target.value);
      updatePayload({ owner: next });
    };

  const onTempPassword = (e: ChangeEvent<HTMLInputElement>): void => {
    const next = setOwnerField(readOwnerDraft(payload), 'tempPassword', e.target.value);
    updatePayload({ owner: next });
  };

  const onModeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value !== 'magic_link' && value !== 'temp_password') return;
    const next = setInviteMode(readOwnerDraft(payload), value);
    updatePayload({ owner: next });
  };

  const emailValidation = validateOwnerEmail(draft.email ?? '');
  const phoneValidation = validateOwnerPhoneOrEmpty(draft.phone ?? '');
  const tempPasswordValidation =
    mode === 'temp_password'
      ? validateTempPassword(draft.tempPassword ?? '')
      : null;
  const completeness = evaluateOwnerCompleteness(draft);

  return (
    <form
      className="ferro-form"
      aria-label="First owner — Step 6"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        We'll seed a single <strong>Owner</strong>-role user for this tenant.
        After commit they receive {mode === 'magic_link'
          ? 'a magic-link invite to set their own password'
          : 'a temporary password and are forced to rotate on first login'}.
      </p>

      <label className="ferro-field">
        <span className="ferro-field__label">Owner email</span>
        <input
          type="email"
          name="ownerEmail"
          autoComplete="email"
          required
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          value={draft.email ?? ''}
          onChange={onField('email')}
          className="ferro-field__input"
          placeholder="ted@happytailhappydog.com"
          aria-invalid={!emailValidation.ok && (draft.email ?? '').length > 0 ? true : undefined}
          aria-describedby="step-owner-email-error"
          data-testid="step-owner-email"
        />
        {!emailValidation.ok && (draft.email ?? '').length > 0 ? (
          <span
            id="step-owner-email-error"
            role="alert"
            className="ferro-form__error"
          >
            {describeEmailError(emailValidation.reason)}
          </span>
        ) : null}
      </label>

      <div className="ferro-wizard__owner-name-row">
        <label className="ferro-field">
          <span className="ferro-field__label">First name</span>
          <input
            type="text"
            name="ownerFirstName"
            autoComplete="given-name"
            required
            value={draft.firstName ?? ''}
            onChange={onField('firstName')}
            className="ferro-field__input"
            placeholder="Ted"
            data-testid="step-owner-first-name"
          />
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Last name</span>
          <input
            type="text"
            name="ownerLastName"
            autoComplete="family-name"
            required
            value={draft.lastName ?? ''}
            onChange={onField('lastName')}
            className="ferro-field__input"
            placeholder="Owner"
            data-testid="step-owner-last-name"
          />
        </label>
      </div>

      <label className="ferro-field">
        <span className="ferro-field__label">
          Phone <span className="ferro-muted">(optional)</span>
        </span>
        <input
          type="tel"
          name="ownerPhone"
          autoComplete="tel"
          inputMode="tel"
          value={draft.phone ?? ''}
          onChange={onField('phone')}
          className="ferro-field__input"
          placeholder="+1-303-555-0199"
          aria-invalid={!phoneValidation.ok ? true : undefined}
          aria-describedby="step-owner-phone-error"
          data-testid="step-owner-phone"
        />
        {!phoneValidation.ok ? (
          <span
            id="step-owner-phone-error"
            role="alert"
            className="ferro-form__error"
          >
            {describePhoneError(phoneValidation.reason)}
          </span>
        ) : null}
      </label>

      <fieldset className="ferro-field">
        <legend className="ferro-field__label">Invite mode</legend>
        <label
          className="ferro-radio"
          style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}
        >
          <input
            type="radio"
            name="ownerInviteMode"
            value="magic_link"
            checked={mode === 'magic_link'}
            onChange={onModeChange}
            data-testid="step-owner-mode-magic_link"
          />
          <span>
            <strong>Magic-link invite</strong>{' '}
            <span className="ferro-muted">
              — owner receives a 7-day single-use link, sets their own password.
            </span>
          </span>
        </label>
        <label
          className="ferro-radio"
          style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}
        >
          <input
            type="radio"
            name="ownerInviteMode"
            value="temp_password"
            checked={mode === 'temp_password'}
            onChange={onModeChange}
            data-testid="step-owner-mode-temp_password"
          />
          <span>
            <strong>Set temporary password</strong>{' '}
            <span className="ferro-muted">
              — owner uses your password on first login and is forced to rotate it.
            </span>
          </span>
        </label>
      </fieldset>

      {mode === 'temp_password' ? (
        <label className="ferro-field" data-testid="step-owner-temp-password-field">
          <span className="ferro-field__label">
            Temporary password{' '}
            <span className="ferro-muted">
              (≥ {TEMP_PASSWORD_MIN_LENGTH} chars, letter + digit)
            </span>
          </span>
          <input
            type="text"
            name="ownerTempPassword"
            autoComplete="new-password"
            required
            spellCheck={false}
            value={draft.tempPassword ?? ''}
            onChange={onTempPassword}
            className="ferro-field__input"
            placeholder="Generate a strong one — owner rotates on first login"
            aria-invalid={
              tempPasswordValidation && !tempPasswordValidation.ok
              && (draft.tempPassword ?? '').length > 0
                ? true
                : undefined
            }
            aria-describedby="step-owner-temp-password-error"
            data-testid="step-owner-temp-password"
          />
          {tempPasswordValidation
          && !tempPasswordValidation.ok
          && (draft.tempPassword ?? '').length > 0 ? (
            <span
              id="step-owner-temp-password-error"
              role="alert"
              className="ferro-form__error"
            >
              {describeTempPasswordError(tempPasswordValidation.reason)}
            </span>
          ) : null}
        </label>
      ) : null}

      <p
        className="ferro-muted"
        data-testid="step-owner-summary"
        aria-live="polite"
      >
        {completeness.ok
          ? `Ready — owner will be created on commit (${
              completeness.mode === 'magic_link' ? 'magic-link invite' : 'temporary password'
            }).`
          : `Owner draft incomplete — ${completeness.issues.length} field${
              completeness.issues.length === 1 ? '' : 's'
            } need${completeness.issues.length === 1 ? 's' : ''} attention before commit.`}
      </p>
    </form>
  );
}
